import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoAsset from '../assets/NamibiaPIPIntelLogo.png';

// One look for every PDF export (the PIP screening report design): teal
// header band with the organisation and CONFIDENTIAL, logo watermark, info
// bar (export date, exported by, search / filters, total records), a
// full-width table whose header repeats on every page, and the confidential
// footer with page numbers.

const TEAL_DARK = [0, 77, 64];
const TEAL      = [0, 121, 107];
const TEAL_LITE = [178, 223, 219];
const WHITE     = [255, 255, 255];

// Colours callers can use in cellStyle.
export const PDF_COLOURS = {
  tealDark: TEAL_DARK,
  green:    [46, 125, 50],
  orange:   [230, 81, 0],
  red:      [183, 28, 28],
  blue:     [21, 101, 192],
  navy:     [13, 71, 161],
  grey:     [97, 97, 97]
};

// The signed-in user: the one passed in, else the copy UserContext keeps.
function resolveUser(user) {
  if (user) return user;
  try { return JSON.parse(localStorage.getItem('user')) || null; } catch (_) { return null; }
}

function loadLogo() {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const w = img.naturalWidth || img.width || 200;
        const h = img.naturalHeight || img.height || 200;
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve({ dataUrl: canvas.toDataURL('image/png'), w, h });
      } catch (_) { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = logoAsset;
  });
}

/**
 * @param {object}   opts
 * @param {string}   opts.title        header title, e.g. 'UN CONSOLIDATED SANCTIONS — SCREENING REPORT'
 * @param {Array}    opts.columns      [{ header, width (relative weight), align? ('left'|'center'|'right'), bold? }]
 * @param {Array[]}  opts.rows         table body — one array of cell values per row
 * @param {string}   [opts.searchValue] what the export was narrowed by (default 'All records')
 * @param {string}   [opts.searchLabel] label for that box (default 'Search / Filters')
 * @param {Function} [opts.cellStyle]  (rowIndex, colIndex) => { textColor?, fontStyle? } | undefined
 * @param {string}   opts.fileName     file name without date / extension, e.g. 'UN_Sanctions_Report'
 * @param {object}   [opts.user]       signed-in user (falls back to the stored one)
 * @param {string}   [opts.orgName]    overrides the organisation shown
 * @param {string}   [opts.userName]   overrides the "exported by" name
 */
export async function exportBrandedPdf({
  title, columns, rows, searchValue, searchLabel = 'Search / Filters',
  cellStyle, fileName, user, orgName, userName
}) {
  const u = resolveUser(user);
  const org = orgName || u?.organisation_name || u?.organization_name || 'PIP Intel';
  const by = userName
    || [u?.first_name, u?.last_name].filter(Boolean).join(' ')
    || u?.email || 'Unknown user';

  const doc   = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();   // 297 mm
  const pageH = doc.internal.pageSize.getHeight();  // 210 mm

  const now     = new Date();
  const dateStr = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  const timeStr = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  const logo = await loadLogo();
  const drawWatermark = () => {
    if (!logo) return;
    const wmW = 90;
    const wmH = (logo.h / logo.w) * wmW;
    doc.saveGraphicsState();
    // eslint-disable-next-line new-cap
    doc.setGState(new doc.GState({ opacity: 0.07 }));
    doc.addImage(logo.dataUrl, 'PNG', (pageW - wmW) / 2, (pageH - wmH) / 2, wmW, wmH);
    doc.restoreGraphicsState();
  };

  const drawPageHeader = () => {
    doc.setFillColor(...TEAL_DARK);
    doc.rect(0, 0, pageW, 22, 'F');
    doc.setFillColor(...TEAL);
    doc.rect(0, 22, pageW, 2.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...WHITE);
    doc.text(title, pageW / 2, 10, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...TEAL_LITE);
    doc.text(`${org}  ·  CONFIDENTIAL`, pageW / 2, 18.5, { align: 'center' });
  };

  // ── Page 1: watermark → header → info bar ──
  drawWatermark();
  drawPageHeader();

  doc.setFillColor(248, 253, 252);
  doc.rect(0, 24.5, pageW, 15, 'F');
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.25);
  doc.line(0, 39.5, pageW, 39.5);

  const colW    = (pageW - 20) / 4;
  const INFO_Y1 = 31;
  const INFO_Y2 = 38;
  const infoItems = [
    { label: 'Export Date',   value: `${dateStr}, ${timeStr}` },
    { label: 'Exported By',   value: by },
    { label: searchLabel,     value: searchValue || 'All records' },
    { label: 'Total Records', value: String(rows.length), big: true }
  ];
  infoItems.forEach(({ label, value, big }, i) => {
    const x = 10 + i * colW;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6);
    doc.setTextColor(...TEAL);
    doc.text(label.toUpperCase(), x, INFO_Y1);
    doc.setFont('helvetica', big ? 'bold' : 'normal');
    doc.setFontSize(big ? 12 : 7.5);
    doc.setTextColor(...(big ? TEAL_DARK : [45, 45, 45]));
    doc.text(String(value), x, INFO_Y2, { maxWidth: colW - 4 });
  });
  for (let i = 1; i < 4; i++) {
    const divX = 10 + i * colW - 4;
    doc.setDrawColor(210, 210, 210);
    doc.setLineWidth(0.2);
    doc.line(divX, INFO_Y1 - 3, divX, INFO_Y2 + 2);
  }

  // ── Full-width table: relative widths shared across the page ──
  const totalWeight = columns.reduce((sum, c) => sum + (c.width || 1), 0);
  const columnStyles = {};
  columns.forEach((c, i) => {
    columnStyles[i] = {
      cellWidth: (pageW * (c.width || 1)) / totalWeight,
      halign: c.align || 'left',
      ...(c.bold ? { fontStyle: 'bold', textColor: TEAL_DARK } : {})
    };
  });

  autoTable(doc, {
    head: [columns.map((c) => c.header)],
    body: rows.map((r) => r.map((v) => (v === null || v === undefined || v === '' ? '—' : String(v)))),
    startY: 42,
    tableWidth: pageW,
    styles: {
      fontSize: 7.5,
      cellPadding: { top: 3.5, bottom: 3.5, left: 4, right: 3 },
      overflow: 'linebreak',
      valign: 'top',
      lineColor: [215, 215, 215],
      lineWidth: 0.2
    },
    headStyles: {
      fillColor: TEAL,
      textColor: WHITE,
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: { top: 4.5, bottom: 4.5, left: 4, right: 3 }
    },
    alternateRowStyles: { fillColor: [240, 250, 249] },
    bodyStyles: { textColor: [40, 40, 40] },
    columnStyles,
    margin: { left: 0, right: 0, bottom: 18 },
    showHead: 'everyPage',
    didParseCell: (data) => {
      if (data.section !== 'body' || !cellStyle) return;
      const st = cellStyle(data.row.index, data.column.index);
      if (st?.textColor) data.cell.styles.textColor = st.textColor;
      if (st?.fontStyle) data.cell.styles.fontStyle = st.fontStyle;
    },
    willDrawPage: (data) => {
      if (data.pageNumber > 1) {
        drawWatermark();
        drawPageHeader();
        doc.setFontSize(7);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(140, 140, 140);
        doc.text(`Continued  ·  ${rows.length} records in total`, pageW / 2, 29, { align: 'center' });
      }
    }
  });

  // ── Footers (total pages now known) ──
  const totalPages = doc.internal.getNumberOfPages();
  for (let pg = 1; pg <= totalPages; pg++) {
    doc.setPage(pg);
    doc.setDrawColor(...TEAL);
    doc.setLineWidth(0.4);
    doc.line(10, pageH - 13, pageW - 10, pageH - 13);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text(`CONFIDENTIAL  ·  ${org}  ·  Generated by ${by}  ·  ${dateStr}`, 10, pageH - 8);
    doc.text(`Page ${pg} of ${totalPages}`, pageW - 10, pageH - 8, { align: 'right' });
  }

  const p = (n) => String(n).padStart(2, '0');
  doc.save(`${fileName}_${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}.pdf`);
}
